import type { ReactNode } from 'react';
import {
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  AI_FILLED_CLASS_NAME,
  useIsAiFilledField,
} from '@/components/ai-quick-fill/ai-filled-fields-context';
import { cn } from '@/lib/utils';
import { Combobox, type ComboboxOption } from './combobox';

export type { ComboboxOption } from './combobox';

interface BaseFieldProps<TForm extends FieldValues> {
  name: FieldPath<TForm>;
  label: string;
  options: readonly ComboboxOption[];
  className?: string | undefined;
  placeholder?: string | undefined;
  searchPlaceholder?: string | undefined;
  emptyMessage?: string | undefined;
  loadingMessage?: string | undefined;
  searchable?: boolean | undefined;
  // Async props — forwarded as-is. Provide all three when the caller
  // drives options from a server query.
  searchValue?: string | undefined;
  onSearchChange?: ((q: string) => void) | undefined;
  loading?: boolean | undefined;
  disabled?: boolean | undefined;
  required?: boolean | undefined;
  description?: ReactNode;
}

export interface SingleComboboxFieldProps<TForm extends FieldValues> extends BaseFieldProps<TForm> {
  multi?: false | undefined;
  // Coerce the selected `value` to a number before writing it back to
  // the form. Use for foreign keys stored as numbers (e.g. company_id).
  asNumber?: boolean | undefined;
  // Opt-in inline create: resolve a typed value to the stored name (or undefined), then select it.
  onCreate?: ((label: string) => Promise<string | undefined> | string | undefined) | undefined;
}

export interface MultiComboboxFieldProps<TForm extends FieldValues> extends BaseFieldProps<TForm> {
  multi: true;
  // Coerce each selected value to a number before writing the array
  // back to the form. Use for FK arrays stored as number[].
  asNumber?: boolean | undefined;
}

export type ComboboxFieldProps<TForm extends FieldValues> =
  | SingleComboboxFieldProps<TForm>
  | MultiComboboxFieldProps<TForm>;

type FieldShape<TForm extends FieldValues> = ControllerRenderProps<TForm, FieldPath<TForm>>;

function readSingle(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

function readMulti(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const v of value) {
    if (typeof v === 'string') out.push(v);
    else if (typeof v === 'number') out.push(String(v));
  }
  return out;
}

function writeSingle<TForm extends FieldValues>(
  field: FieldShape<TForm>,
  asNumber: boolean | undefined,
) {
  return (next: string | undefined) => {
    if (next === undefined) field.onChange(undefined);
    else if (asNumber === true) field.onChange(Number(next));
    else field.onChange(next);
  };
}

function writeMulti<TForm extends FieldValues>(
  field: FieldShape<TForm>,
  asNumber: boolean | undefined,
) {
  return (next: string[]) => {
    field.onChange(asNumber === true ? next.map((v) => Number(v)) : next);
  };
}

interface ForwardedProps {
  options: readonly ComboboxOption[];
  placeholder: string | undefined;
  searchPlaceholder: string | undefined;
  emptyMessage: string | undefined;
  loadingMessage: string | undefined;
  searchable: boolean | undefined;
  searchValue: string | undefined;
  onSearchChange: ((q: string) => void) | undefined;
  loading: boolean | undefined;
  disabled: boolean | undefined;
}

function pickForwarded<TForm extends FieldValues>(
  props: Readonly<BaseFieldProps<TForm>>,
): ForwardedProps {
  return {
    options: props.options,
    placeholder: props.placeholder,
    searchPlaceholder: props.searchPlaceholder,
    emptyMessage: props.emptyMessage,
    loadingMessage: props.loadingMessage,
    searchable: props.searchable,
    searchValue: props.searchValue,
    onSearchChange: props.onSearchChange,
    loading: props.loading,
    disabled: props.disabled,
  };
}

function renderSingle<TForm extends FieldValues>(
  field: FieldShape<TForm>,
  props: Readonly<SingleComboboxFieldProps<TForm>>,
): React.JSX.Element {
  const write = writeSingle(field, props.asNumber);
  const create = props.onCreate;
  const onCreate =
    create === undefined
      ? undefined
      : (label: string) => {
          void Promise.resolve(create(label)).then((name) => {
            if (name !== undefined) write(name);
          });
        };
  return (
    <Combobox
      value={readSingle(field.value)}
      onChange={write}
      onCreate={onCreate}
      {...pickForwarded(props)}
    />
  );
}

function renderMulti<TForm extends FieldValues>(
  field: FieldShape<TForm>,
  props: Readonly<MultiComboboxFieldProps<TForm>>,
): React.JSX.Element {
  return (
    <Combobox
      multi
      value={readMulti(field.value)}
      onChange={writeMulti(field, props.asNumber)}
      {...pickForwarded(props)}
    />
  );
}

export function ComboboxField<TForm extends FieldValues>(
  props: Readonly<ComboboxFieldProps<TForm>>,
) {
  const form = useFormContext<TForm>();
  const aiFilled = useIsAiFilledField(props.name);
  return (
    <FormField
      control={form.control}
      name={props.name}
      render={({ field }) => (
        <FormItem className={cn(props.className, aiFilled && AI_FILLED_CLASS_NAME)}>
          <FormLabel required={props.required}>{props.label}</FormLabel>
          <FormControl required={props.required}>
            {props.multi === true ? renderMulti(field, props) : renderSingle(field, props)}
          </FormControl>
          {props.description !== undefined && (
            <FormDescription>{props.description}</FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
